import React, { useEffect, useState } from 'react'
import SaveComponent from './Save'
import { saveBoards } from '../../flux/boards/BoardActions'
import BoardStore, {
    EventsEnum as StoreEvents,
} from '../../flux/boards/BoardStore'
import SwimlaneStore, {
    EventsEnum as SwimlaneEvents,
} from '../../flux/swimlanes/SwimlaneStore'
import TaskStore, { EventsEnum as TaskEvents } from '../../flux/tasks/TaskStore'
import SettingsStore from '../../flux/settings/SettingsStore'
import {
    autoSaveChanged,
    saveSettings,
} from '../../flux/settings/SettingsActions'

/**
 * Renders the save component.
 * Handles user requests related to saving data.
 */
function ControlSaveComponent() {
    const settingsStore = SettingsStore.GetInstance()
    const [autoSave, setAutoSave] = useState(settingsStore.AutoSave)
    const [canUseLocalStorage, setCanUseLocalStorage] = useState(
        settingsStore.CanUseLocalStorage
    )

    /**
     * Handles save event triggered from save component
     */
    function handleSave() {
        saveBoards()
    }

    /**
     * Handles the user changing the autosave state
     */
    function handleSaveToggle() {
        const newAutoSave = !autoSave
        autoSaveChanged(newAutoSave)
        if (newAutoSave) saveBoards()
    }

    useEffect(() => {
        /**
         * Handles toggling of autosave setting
         */
        function handleSettingsChange() {
            setCanUseLocalStorage(settingsStore.CanUseLocalStorage)
            if (settingsStore.CanUseLocalStorage) {
                saveSettings()
                setAutoSave(settingsStore.AutoSave)
            }
        }

        /**
         * Handles cleaning up the component on unmount
         */
        function handleCleanup() {
            // unbind settings store change listener
            settingsStore.removeChangeListener(handleSettingsChange)
            // no local storage? just return
            if (!settingsStore.CanUseLocalStorage) return
            // no autosave? just return
            if (!autoSave) return
            // autosave is on
            BoardStore.removeBoardStoreListener(StoreEvents.ADD, handleSave)
            BoardStore.removeBoardStoreListener(StoreEvents.EDIT, handleSave)
            BoardStore.removeBoardStoreListener(StoreEvents.DELETE, handleSave)
            BoardStore.removeBoardStoreListener(StoreEvents.MOVE, handleSave)
            SwimlaneStore.removeSwimlaneStoreListener(
                SwimlaneEvents.ADD,
                handleSave
            )
            SwimlaneStore.removeSwimlaneStoreListener(
                SwimlaneEvents.EDIT,
                handleSave
            )
            SwimlaneStore.removeSwimlaneStoreListener(
                SwimlaneEvents.DELETE,
                handleSave
            )
            SwimlaneStore.removeSwimlaneStoreListener(
                SwimlaneEvents.MOVE,
                handleSave
            )
            TaskStore.removeTaskStoreListener(TaskEvents.ADD, handleSave)
            TaskStore.removeTaskStoreListener(TaskEvents.EDIT, handleSave)
            TaskStore.removeTaskStoreListener(TaskEvents.DELETE, handleSave)
            TaskStore.removeTaskStoreListener(TaskEvents.INSERT, handleSave)
            TaskStore.removeTaskStoreListener(TaskEvents.MOVE, handleSave)
        }
        // bind settings change listener as local storage is available
        settingsStore.addChangeListener(handleSettingsChange)
        // no local storage? just return
        if (!settingsStore.CanUseLocalStorage) return handleCleanup
        // no autosave? don't bind store listeners
        if (!autoSave) return handleCleanup
        // autosave is on
        BoardStore.addBoardStoreListener(StoreEvents.ADD, handleSave)
        BoardStore.addBoardStoreListener(StoreEvents.EDIT, handleSave)
        BoardStore.addBoardStoreListener(StoreEvents.DELETE, handleSave)
        BoardStore.addBoardStoreListener(StoreEvents.MOVE, handleSave)
        SwimlaneStore.addSwimlaneStoreListener(SwimlaneEvents.ADD, handleSave)
        SwimlaneStore.addSwimlaneStoreListener(SwimlaneEvents.EDIT, handleSave)
        SwimlaneStore.addSwimlaneStoreListener(
            SwimlaneEvents.DELETE,
            handleSave
        )
        SwimlaneStore.addSwimlaneStoreListener(SwimlaneEvents.MOVE, handleSave)
        TaskStore.addTaskStoreListener(TaskEvents.ADD, handleSave)
        TaskStore.addTaskStoreListener(TaskEvents.EDIT, handleSave)
        TaskStore.addTaskStoreListener(TaskEvents.DELETE, handleSave)
        TaskStore.addTaskStoreListener(TaskEvents.INSERT, handleSave)
        TaskStore.addTaskStoreListener(TaskEvents.MOVE, handleSave)
        return handleCleanup
    }, [autoSave, settingsStore])

    return (
        <>
            {canUseLocalStorage && (
                <SaveComponent
                    autoSave={autoSave}
                    onSave={handleSave}
                    onAutosaveToggle={handleSaveToggle}
                />
            )}
        </>
    )
}

export default ControlSaveComponent
